import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Glasses,
  Plus,
  Trash2,
  Edit2,
  X,
  Box,
  Image as ImageIcon,
  AlertCircle,
  Check,
  Upload,
  Eye,
  ZoomIn,
  ZoomOut,
  Crop,
  Link as LinkIcon,
  Loader2,
  Play,
  RotateCw,
  RotateCcw,
  Undo2,
  Move,
  FlipHorizontal,
  FlipVertical,
  Save,
  RefreshCw,
  Wand2,
  Eraser,
  PaintBucket,
  MousePointer2,
  Sun,
  Feather,
  LayoutGrid,
  List,
  GripVertical,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { products as productsApi } from '../api';
import VirtualTryOn from '../../VirtualTryOn';
import { removeBackgroundWorkersAI } from '../../services/imageProcessingService';
import { computeAlphaBoundsFromDataUrl, computeAlphaBoundsFromImage } from '../../utils/alphaBounds';

// ============================================
// IMAGE UTILITIES
// ============================================

// Convert file to base64 data URL
const fileToDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Apply transformations to image and return new data URL
const applyImageTransform = (imageSrc, transform) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const { rotation, flipH, flipV, offsetX, offsetY } = transform;
      
      // Calculate canvas size based on rotation
      const radians = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(radians));
      const cos = Math.abs(Math.cos(radians));
      const newWidth = img.width * cos + img.height * sin;
      const newHeight = img.width * sin + img.height * cos;
      
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Move to center
      ctx.translate(newWidth / 2, newHeight / 2);
      
      // Apply rotation
      ctx.rotate(radians);
      
      // Apply flips
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      
      // Draw image centered with offset
      ctx.drawImage(
        img, 
        -img.width / 2 + offsetX, 
        -img.height / 2 + offsetY
      );
      
      // Export as PNG to preserve transparency
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(imageSrc);
    img.src = imageSrc;
  });
};

// ============================================
// IMAGE TRANSFORM EDITOR MODAL
// ============================================

const ImageTransformEditor = ({ imageSrc, label, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const workingCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const [transform, setTransform] = useState({
    rotation: 0,
    flipH: false,
    flipV: false,
    offsetX: 0,
    offsetY: 0,
  });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tool, setTool] = useState('transform');
  const [eraserSize, setEraserSize] = useState(24);
  const [magicEraserTolerance, setMagicEraserTolerance] = useState(40); // default Standard
  const [magicEraserTransparency, setMagicEraserTransparency] = useState(20); // default 20% transparency (0=no change, 100=full erase)
  const [bucketFillColor, setBucketFillColor] = useState('#ffffff'); // hex or 'transparent'
  const [eraserCursor, setEraserCursor] = useState(null); // { x, y, radius } in container px when tool is eraser
  const imgRef = useRef(null);
  const isDrawingRef = useRef(false);
  const cropRectRef = useRef(null);

  // Crop: selection in preview coords { x1, y1, x2, y2 }
  const [cropRect, setCropRect] = useState(null);
  cropRectRef.current = cropRect;
  const cropStartRef = useRef(null);
  const [cropAspectRatio, setCropAspectRatio] = useState(null); // null = free, or number e.g. 1, 4/3, 16/9
  const cropDragRef = useRef(null); // 'move' | 'n'|'s'|'e'|'w'|'nw'|'ne'|'sw'|'se'
  const cropDragStartRef = useRef(null); // { rect: {x1,y1,x2,y2}, startX, startY }
  const CROP_HANDLE_SIZE = 10;
  const MIN_CROP_SIZE = 20;
  // Magic select: mask Uint8Array(w*h) of selected pixels, 1=selected
  const magicSelectionMaskRef = useRef(null);
  const [hasMagicSelection, setHasMagicSelection] = useState(false);
  // Zoom: 0.5 .. 2, viewSize = preview pixel size
  const [zoom, setZoom] = useState(1);
  const [viewSize, setViewSize] = useState({ w: 400, h: 300 });
  const [removingBG, setRemovingBG] = useState(false);
  const [effectiveImageSrc, setEffectiveImageSrc] = useState(imageSrc);
  const [undoHistory, setUndoHistory] = useState([]);
  const MAX_UNDO = 20;

  useEffect(() => {
    setEffectiveImageSrc(imageSrc);
  }, [imageSrc]);

  // Draw preview from working canvas (checkerboard + scaled working image) + optional crop / magic-select overlay
  const drawPreviewFromWorking = useCallback((cropOverlay = null, magicMask = null) => {
    const preview = canvasRef.current;
    const working = workingCanvasRef.current;
    if (!preview || !working || working.width === 0 || working.height === 0) return;

    const maxSize = 720;
    const scale = Math.min(maxSize / working.width, maxSize / working.height, 1);
    const previewW = Math.round(working.width * scale);
    const previewH = Math.round(working.height * scale);
    preview.width = previewW;
    preview.height = previewH;
    setViewSize({ w: previewW, h: previewH });

    const ctx = preview.getContext('2d');
    const patternSize = 10;
    for (let x = 0; x < preview.width; x += patternSize) {
      for (let y = 0; y < preview.height; y += patternSize) {
        ctx.fillStyle = ((x + y) / patternSize) % 2 === 0 ? '#1a1a24' : '#252532';
        ctx.fillRect(x, y, patternSize, patternSize);
      }
    }
    ctx.drawImage(working, 0, 0, working.width, working.height, 0, 0, previewW, previewH);

    const overlay = cropOverlay ?? cropRectRef.current;
    if (overlay && overlay.x2 > overlay.x1 && overlay.y2 > overlay.y1) {
      const { x1, y1, x2, y2 } = overlay;
      const tile = 8;
      for (let py = 0; py < previewH; py += tile) {
        for (let px = 0; px < previewW; px += tile) {
          const inSelection = px < x2 && px + tile > x1 && py < y2 && py + tile > y1;
          if (!inSelection) {
            ctx.fillStyle = ((px + py) / tile) % 2 === 0 ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.15)';
            ctx.fillRect(px, py, tile, tile);
          }
        }
      }
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.setLineDash([]);
      // Resize handles (corners + edges)
      const hs = 6;
      ctx.fillStyle = '#3b82f6';
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      const handles = [
        [x1, y1], [x2, y1], [x1, y2], [x2, y2],
        [(x1 + x2) / 2, y1], [(x1 + x2) / 2, y2], [x1, (y1 + y2) / 2], [x2, (y1 + y2) / 2],
      ];
      handles.forEach(([hx, hy]) => {
        ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
        ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
      });
    }
    const mask = magicMask ?? magicSelectionMaskRef.current;
    if (mask && mask.data && mask.w > 0 && mask.h > 0) {
      const m = mask.data;
      const mw = mask.w;
      const mh = mask.h;
      const overlayData = ctx.getImageData(0, 0, previewW, previewH);
      const od = overlayData.data;
      for (let sy = 0; sy < previewH; sy++) {
        for (let sx = 0; sx < previewW; sx++) {
          const my = Math.floor((sy / previewH) * mh);
          const mx = Math.floor((sx / previewW) * mw);
          if (my >= 0 && my < mh && mx >= 0 && mx < mw && m[my * mw + mx]) {
            const i = (sy * previewW + sx) * 4;
            od[i] = 255;
            od[i + 1] = 100;
            od[i + 2] = 100;
            od[i + 3] = 140;
          }
        }
      }
      ctx.putImageData(overlayData, 0, 0);
    }
  }, []);

  // Create working canvas once
  useEffect(() => {
    if (!workingCanvasRef.current) {
      workingCanvasRef.current = document.createElement('canvas');
    }
  }, []);

  // Load effective image (initial or after crop/erase/removeBG) into imgRef
  useEffect(() => {
    if (!effectiveImageSrc || !canvasRef.current) return;
    setImgLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.src = effectiveImageSrc;
  }, [effectiveImageSrc]);

  useEffect(() => {
    if (!effectiveImageSrc || !imgRef.current || !workingCanvasRef.current) return;
    let cancelled = false;
    applyImageTransform(effectiveImageSrc, transform).then((dataUrl) => {
      if (cancelled) return;
      const out = new Image();
      out.crossOrigin = 'anonymous';
      out.onload = () => {
        if (cancelled) return;
        const w = workingCanvasRef.current;
        if (!w) return;
        w.width = out.width;
        w.height = out.height;
        const ctx = w.getContext('2d');
        ctx.clearRect(0, 0, w.width, w.height);
        ctx.drawImage(out, 0, 0);
        drawPreviewFromWorking(null);
      };
      out.onerror = () => { if (!cancelled) drawPreviewFromWorking(null); };
      out.src = dataUrl;
    });
    return () => { cancelled = true; };
  }, [effectiveImageSrc, transform, imgLoaded, drawPreviewFromWorking]);

  // Redraw overlay when crop or magic selection changes
  useEffect(() => {
    if (canvasRef.current && workingCanvasRef.current?.width) {
      drawPreviewFromWorking(cropRect, hasMagicSelection ? magicSelectionMaskRef.current : null);
    }
  }, [cropRect, hasMagicSelection, drawPreviewFromWorking]);

  const handleRotate = (delta) => {
    setTransform(prev => ({
      ...prev,
      rotation: (prev.rotation + delta + 360) % 360
    }));
  };

  const handleReset = useCallback(() => {
    setEffectiveImageSrc(imageSrc);
    setTransform({
      rotation: 0,
      flipH: false,
      flipV: false,
      offsetX: 0,
      offsetY: 0,
    });
    setCropRect(null);
    cropStartRef.current = null;
    cropDragRef.current = null;
    magicSelectionMaskRef.current = null;
    setHasMagicSelection(false);
  }, [imageSrc]);

  // Map pointer to preview canvas pixel coords (for crop overlay)
  const pointerToPreview = useCallback((clientX, clientY) => {
    const preview = canvasRef.current;
    if (!preview || preview.width === 0 || preview.height === 0) return null;
    const rect = preview.getBoundingClientRect();
    const scaleX = preview.width / rect.width;
    const scaleY = preview.height / rect.height;
    const px = (clientX - rect.left) * scaleX;
    const py = (clientY - rect.top) * scaleY;
    return { x: Math.max(0, Math.min(preview.width, px)), y: Math.max(0, Math.min(preview.height, py)) };
  }, []);

  // Map pointer position (in container/canvas display coords) to working canvas pixel coords.
  // When allowOutside is true (e.g. for eraser), clamp to canvas bounds so you can keep drawing at the edge when cursor is outside.
  const pointerToWorking = useCallback((clientX, clientY, allowOutside = false) => {
    const preview = canvasRef.current;
    const working = workingCanvasRef.current;
    if (!preview || !working || preview.width === 0 || preview.height === 0) return null;
    const rect = preview.getBoundingClientRect();
    const scaleX = preview.width / rect.width;
    const scaleY = preview.height / rect.height;
    let px = (clientX - rect.left) * scaleX;
    let py = (clientY - rect.top) * scaleY;
    if (!allowOutside && (px < 0 || py < 0 || px > preview.width || py > preview.height)) return null;
    if (allowOutside) {
      px = Math.max(0, Math.min(preview.width, px));
      py = Math.max(0, Math.min(preview.height, py));
    }
    const wx = (px / preview.width) * working.width;
    const wy = (py / preview.height) * working.height;
    return { x: wx, y: wy };
  }, []);

  // Undo: save current working canvas before modifications, restore on Undo
  const pushState = useCallback(() => {
    const w = workingCanvasRef.current;
    if (!w || w.width === 0 || w.height === 0) return;
    const url = w.toDataURL('image/png');
    setUndoHistory((h) => {
      const next = [...h, url];
      return next.slice(-MAX_UNDO);
    });
  }, []);

  const restoreState = useCallback((dataUrl) => {
    const img = new Image();
    img.onload = () => {
      const w = workingCanvasRef.current;
      if (!w) return;
      w.width = img.width;
      w.height = img.height;
      w.getContext('2d').drawImage(img, 0, 0);
      setEffectiveImageSrc(dataUrl);
      magicSelectionMaskRef.current = null;
      setHasMagicSelection(false);
      drawPreviewFromWorking(null);
    };
    img.src = dataUrl;
  }, [drawPreviewFromWorking]);

  const handleUndo = useCallback(() => {
    setUndoHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      restoreState(prev);
      return h.slice(0, -1);
    });
  }, [restoreState]);

  const eraseAt = useCallback((wx, wy) => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0) return;
    const ctx = working.getContext('2d');
    const r = Math.max(2, eraserSize);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(wx, wy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    drawPreviewFromWorking();
  }, [eraserSize, drawPreviewFromWorking]);

  // Magic eraser: flood-fill similar color at (wx,wy) within tolerance, set alpha to 0
  const magicEraseAt = useCallback((wx, wy) => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0 || working.height === 0) return;
    pushState();
    const ctx = working.getContext('2d');
    const w = working.width;
    const h = working.height;
    const ix = Math.floor(wx);
    const iy = Math.floor(wy);
    if (ix < 0 || ix >= w || iy < 0 || iy >= h) return;
    const data = ctx.getImageData(0, 0, w, h);
    const pixels = data.data;
    const idx0 = (iy * w + ix) * 4;
    const r0 = pixels[idx0];
    const g0 = pixels[idx0 + 1];
    const b0 = pixels[idx0 + 2];
    const a0 = pixels[idx0 + 3];
    if (a0 < 10) return; // already transparent
    const tol = Math.max(1, Math.min(120, (magicEraserTolerance / 100) * 150));
    const visited = new Uint8Array(w * h);
    const stack = [[ix, iy]];
    const getIdx = (x, y) => (y * w + x) * 4;
    const colorDist = (i) => {
      const dr = pixels[i] - r0;
      const dg = pixels[i + 1] - g0;
      const db = pixels[i + 2] - b0;
      return Math.max(Math.abs(dr), Math.abs(dg), Math.abs(db));
    };
    let count = 0;
    const maxPixels = w * h;
    while (stack.length > 0 && count < maxPixels) {
      const [x, y] = stack.pop();
      if (x < 0 || x >= w || y < 0 || y >= h) continue;
      const vidx = y * w + x;
      if (visited[vidx]) continue;
      const i = getIdx(x, y);
      if (pixels[i + 3] < 10) continue;
      if (colorDist(i) > tol) continue;
      visited[vidx] = 1;
      // Apply transparency: 0 = no change, 100 = full erase (alpha 0)
      const newAlpha = Math.round(pixels[i + 3] * (1 - magicEraserTransparency / 100));
      pixels[i + 3] = Math.max(0, Math.min(255, newAlpha));
      count++;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    ctx.putImageData(data, 0, 0);
    drawPreviewFromWorking();
    setEffectiveImageSrc(working.toDataURL('image/png'));
  }, [magicEraserTolerance, magicEraserTransparency, drawPreviewFromWorking, pushState]);

  // Magic select: click to select similar colors, then "Delete selection" to erase (same flood-fill as magic eraser but store in mask)
  const magicSelectAt = useCallback((wx, wy) => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0 || working.height === 0) return;
    const ctx = working.getContext('2d');
    const w = working.width;
    const h = working.height;
    const imageData = ctx.getImageData(0, 0, w, h);
    const pixels = imageData.data;
    const ix = Math.floor(wx);
    const iy = Math.floor(wy);
    if (ix < 0 || ix >= w || iy < 0 || iy >= h) return;
    const idx0 = (iy * w + ix) * 4;
    if (pixels[idx0 + 3] < 10) return;
    const r0 = pixels[idx0];
    const g0 = pixels[idx0 + 1];
    const b0 = pixels[idx0 + 2];
    const tol = Math.max(1, Math.min(120, (magicEraserTolerance / 100) * 150));
    const mask = new Uint8Array(w * h);
    const visited = new Uint8Array(w * h);
    const stack = [[ix, iy]];
    const getIdx = (x, y) => (y * w + x) * 4;
    const colorDist = (i) => Math.max(
      Math.abs(pixels[i] - r0),
      Math.abs(pixels[i + 1] - g0),
      Math.abs(pixels[i + 2] - b0)
    );
    let count = 0;
    while (stack.length > 0 && count < w * h) {
      const [x, y] = stack.pop();
      if (x < 0 || x >= w || y < 0 || y >= h) continue;
      const vidx = y * w + x;
      if (visited[vidx]) continue;
      const i = getIdx(x, y);
      if (pixels[i + 3] < 10) continue;
      if (colorDist(i) > tol) continue;
      visited[vidx] = 1;
      mask[vidx] = 1;
      count++;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    magicSelectionMaskRef.current = { data: mask, w, h };
    setHasMagicSelection(true);
    drawPreviewFromWorking(cropRectRef.current, { data: mask, w, h });
  }, [magicEraserTolerance, drawPreviewFromWorking]);

  // Bucket fill: flood-fill similar colors at (wx,wy) with selected color (or transparent)
  const bucketFillAt = useCallback((wx, wy) => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0 || working.height === 0) return;
    pushState();
    const ctx = working.getContext('2d');
    const w = working.width;
    const h = working.height;
    const ix = Math.floor(wx);
    const iy = Math.floor(wy);
    if (ix < 0 || ix >= w || iy < 0 || iy >= h) return;
    const data = ctx.getImageData(0, 0, w, h);
    const pixels = data.data;
    const idx0 = (iy * w + ix) * 4;
    const r0 = pixels[idx0];
    const g0 = pixels[idx0 + 1];
    const b0 = pixels[idx0 + 2];
    const a0 = pixels[idx0 + 3];
    const tol = Math.max(1, Math.min(120, (magicEraserTolerance / 100) * 150));
    const visited = new Uint8Array(w * h);
    const stack = [[ix, iy]];
    const getIdx = (x, y) => (y * w + x) * 4;
    const colorDist = (i) => {
      const dr = pixels[i] - r0;
      const dg = pixels[i + 1] - g0;
      const db = pixels[i + 2] - b0;
      return Math.max(Math.abs(dr), Math.abs(dg), Math.abs(db));
    };
    const isTransparent = bucketFillColor === 'transparent';
    let fr = 0, fg = 0, fb = 0, fa = 255;
    if (!isTransparent) {
      const hex = bucketFillColor.replace(/^#/, '');
      if (hex.length === 6) {
        fr = parseInt(hex.slice(0, 2), 16);
        fg = parseInt(hex.slice(2, 4), 16);
        fb = parseInt(hex.slice(4, 6), 16);
      }
    } else {
      fa = 0;
    }
    let count = 0;
    const maxPixels = w * h;
    while (stack.length > 0 && count < maxPixels) {
      const [x, y] = stack.pop();
      if (x < 0 || x >= w || y < 0 || y >= h) continue;
      const vidx = y * w + x;
      if (visited[vidx]) continue;
      const i = getIdx(x, y);
      if (pixels[i + 3] < 10) continue;
      if (colorDist(i) > tol) continue;
      visited[vidx] = 1;
      pixels[i] = fr;
      pixels[i + 1] = fg;
      pixels[i + 2] = fb;
      pixels[i + 3] = fa;
      count++;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    ctx.putImageData(data, 0, 0);
    drawPreviewFromWorking();
    setEffectiveImageSrc(working.toDataURL('image/png'));
  }, [magicEraserTolerance, bucketFillColor, drawPreviewFromWorking, pushState]);

  // Hit-test: which crop handle or 'move' is at (px, py)? Corners first, then edges, then move. Defined before handlePointerDown.
  const getCropHandleAt = useCallback((px, py, rect) => {
    if (!rect || rect.x2 <= rect.x1 || rect.y2 <= rect.y1) return null;
    const { x1, y1, x2, y2 } = rect;
    const s = CROP_HANDLE_SIZE;
    if (px >= x1 - s && px <= x1 + s && py >= y1 - s && py <= y1 + s) return 'nw';
    if (px >= x2 - s && px <= x2 + s && py >= y1 - s && py <= y1 + s) return 'ne';
    if (px >= x1 - s && px <= x1 + s && py >= y2 - s && py <= y2 + s) return 'sw';
    if (px >= x2 - s && px <= x2 + s && py >= y2 - s && py <= y2 + s) return 'se';
    if (py >= y1 - s && py <= y1 + s && px >= x1 + s && px <= x2 - s) return 'n';
    if (py >= y2 - s && py <= y2 + s && px >= x1 + s && px <= x2 - s) return 's';
    if (px >= x1 - s && px <= x1 + s && py >= y1 + s && py <= y2 - s) return 'w';
    if (px >= x2 - s && px <= x2 + s && py >= y1 + s && py <= y2 - s) return 'e';
    if (px >= x1 + s && px <= x2 - s && py >= y1 + s && py <= y2 - s) return 'move';
    return null;
  }, []);

  const handlePointerDown = useCallback((e) => {
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    if (tool === 'eraser') {
      e.preventDefault();
      const p = pointerToWorking(clientX, clientY, true); // allow outside so eraser can smoothen edges at border
      if (p) {
        isDrawingRef.current = true;
        if (containerRef.current && e.pointerId != null) containerRef.current.setPointerCapture(e.pointerId);
        pushState();
        eraseAt(p.x, p.y);
      }
    } else if (tool === 'magicEraser') {
      e.preventDefault();
      const p = pointerToWorking(clientX, clientY);
      if (p) magicEraseAt(p.x, p.y);
    } else if (tool === 'crop') {
      e.preventDefault();
      const p = pointerToPreview(clientX, clientY);
      if (!p) return;
      const rect = cropRectRef.current;
      if (rect && rect.x2 > rect.x1 && rect.y2 > rect.y1) {
        const handle = getCropHandleAt(p.x, p.y, rect);
        if (handle) {
          cropDragRef.current = handle;
          cropDragStartRef.current = { rect: { ...rect }, startX: p.x, startY: p.y };
          return;
        }
      }
      cropStartRef.current = p;
      setCropRect({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    } else if (tool === 'magicSelect') {
      e.preventDefault();
      const p = pointerToWorking(clientX, clientY);
      if (p) magicSelectAt(p.x, p.y);
    } else if (tool === 'bucketFill') {
      e.preventDefault();
      const p = pointerToWorking(clientX, clientY);
      if (p) bucketFillAt(p.x, p.y);
    }
  }, [tool, pointerToWorking, pointerToPreview, eraseAt, magicEraseAt, magicSelectAt, bucketFillAt, pushState, getCropHandleAt]);

  const handlePointerMove = useCallback((e) => {
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    if (tool === 'eraser') {
      const el = containerRef.current;
      const canvas = canvasRef.current;
      const working = workingCanvasRef.current;
      if (el && canvas && working?.width > 0) {
        const rect = el.getBoundingClientRect();
        const inBounds = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
        if (inBounds) {
          const canvasRect = canvas.getBoundingClientRect();
          const scale = canvasRect.width / working.width;
          const radius = Math.max(6, Math.min(150, eraserSize * scale));
          setEraserCursor({ x: clientX - rect.left, y: clientY - rect.top, radius });
        } else {
          setEraserCursor(null);
        }
      } else {
        setEraserCursor(null);
      }
      if (isDrawingRef.current) {
        e.preventDefault();
        const p = pointerToWorking(clientX, clientY, true); // allow outside so eraser continues at edge
        if (p) eraseAt(p.x, p.y);
      }
    } else {
      setEraserCursor(null);
      if (tool === 'crop') {
        const p = pointerToPreview(clientX, clientY);
        if (!p) return;
        const preview = canvasRef.current;
        const previewW = preview?.width ?? 0;
        const previewH = preview?.height ?? 0;
        if (cropDragRef.current) {
          e.preventDefault();
          const handle = cropDragRef.current;
          const start = cropDragStartRef.current;
          if (!start) return;
          const { rect: r } = start;
          const ratio = cropAspectRatio;
          let x1 = r.x1, y1 = r.y1, x2 = r.x2, y2 = r.y2;
          if (handle === 'move') {
            const dx = p.x - start.startX;
            const dy = p.y - start.startY;
            const w = r.x2 - r.x1, h = r.y2 - r.y1;
            x1 = Math.max(0, Math.min(previewW - w, r.x1 + dx));
            y1 = Math.max(0, Math.min(previewH - h, r.y1 + dy));
            x2 = x1 + w;
            y2 = y1 + h;
          } else {
            if (handle.includes('w')) x1 = Math.max(0, Math.min(r.x2 - MIN_CROP_SIZE, p.x));
            if (handle.includes('e')) x2 = Math.min(previewW, Math.max(r.x1 + MIN_CROP_SIZE, p.x));
            if (handle.includes('n')) y1 = Math.max(0, Math.min(r.y2 - MIN_CROP_SIZE, p.y));
            if (handle.includes('s')) y2 = Math.min(previewH, Math.max(r.y1 + MIN_CROP_SIZE, p.y));
            if (ratio != null && ratio > 0) {
              let w = x2 - x1, h = y2 - y1;
              if (w / h > ratio) h = w / ratio;
              else w = h * ratio;
              if (handle.includes('e')) x2 = x1 + w;
              if (handle.includes('w')) x1 = x2 - w;
              if (handle.includes('s')) y2 = y1 + h;
              if (handle.includes('n')) y1 = y2 - h;
              if (handle.includes('e') || handle.includes('w')) y2 = y1 + h;
              if (handle.includes('s') || handle.includes('n')) x2 = x1 + w;
            }
            if (x2 <= x1) x2 = x1 + MIN_CROP_SIZE;
            if (y2 <= y1) y2 = y1 + MIN_CROP_SIZE;
            x1 = Math.max(0, x1);
            y1 = Math.max(0, y1);
            x2 = Math.min(previewW, x2);
            y2 = Math.min(previewH, y2);
          }
          setCropRect({ x1, y1, x2, y2 });
          cropDragStartRef.current = { rect: { x1, y1, x2, y2 }, startX: p.x, startY: p.y };
        } else if (cropStartRef.current) {
          e.preventDefault();
          const s = cropStartRef.current;
          let x1 = Math.min(s.x, p.x), y1 = Math.min(s.y, p.y), x2 = Math.max(s.x, p.x), y2 = Math.max(s.y, p.y);
          if (cropAspectRatio != null && cropAspectRatio > 0) {
            const w = x2 - x1, h = y2 - y1;
            if (w / h > cropAspectRatio) y2 = y1 + w / cropAspectRatio;
            else x2 = x1 + h * cropAspectRatio;
          }
          setCropRect({ x1, y1, x2, y2 });
        }
      }
    }
  }, [tool, eraserSize, pointerToWorking, pointerToPreview, eraseAt, cropAspectRatio]);

  const handlePointerUp = useCallback((e) => {
    if (tool === 'eraser' && isDrawingRef.current) {
      const working = workingCanvasRef.current;
      if (working?.width > 0 && working?.height > 0) {
        setEffectiveImageSrc(working.toDataURL('image/png'));
      }
      if (containerRef.current && e?.pointerId != null) containerRef.current.releasePointerCapture(e.pointerId);
    }
    isDrawingRef.current = false;
    if (tool === 'crop') {
      cropStartRef.current = null;
      cropDragRef.current = null;
    }
    setEraserCursor(null);
  }, [tool]);

  const clearCropSelection = useCallback(() => {
    setCropRect(null);
    cropStartRef.current = null;
    cropDragRef.current = null;
    drawPreviewFromWorking(null);
  }, [drawPreviewFromWorking]);

  const clearMagicSelection = useCallback(() => {
    magicSelectionMaskRef.current = null;
    setHasMagicSelection(false);
    drawPreviewFromWorking(cropRectRef.current, null);
  }, [drawPreviewFromWorking]);

  const applyMagicSelectionErase = useCallback(() => {
    const working = workingCanvasRef.current;
    const maskRef = magicSelectionMaskRef.current;
    if (!working || !maskRef || !maskRef.data) return;
    pushState();
    const ctx = working.getContext('2d');
    const imageData = ctx.getImageData(0, 0, working.width, working.height);
    const data = imageData.data;
    const m = maskRef.data;
    const len = m.length;
    for (let i = 0; i < len; i++) {
      if (m[i]) data[i * 4 + 3] = 0;
    }
    ctx.putImageData(imageData, 0, 0);
    magicSelectionMaskRef.current = null;
    setHasMagicSelection(false);
    setEffectiveImageSrc(working.toDataURL('image/png'));
    drawPreviewFromWorking(cropRectRef.current, null);
  }, [drawPreviewFromWorking, pushState]);

  // Crop: convert preview rect to working coords, crop working canvas, clear selection
  const applyCrop = useCallback(() => {
    const working = workingCanvasRef.current;
    const preview = canvasRef.current;
    if (!working || !preview || !cropRect || cropRect.x2 <= cropRect.x1 || cropRect.y2 <= cropRect.y1) return;
    pushState();
    const previewW = preview.width;
    const previewH = preview.height;
    const wx1 = Math.floor((cropRect.x1 / previewW) * working.width);
    const wy1 = Math.floor((cropRect.y1 / previewH) * working.height);
    const ww = Math.max(1, Math.floor(((cropRect.x2 - cropRect.x1) / previewW) * working.width));
    const wh = Math.max(1, Math.floor(((cropRect.y2 - cropRect.y1) / previewH) * working.height));
    const temp = document.createElement('canvas');
    temp.width = ww;
    temp.height = wh;
    const tctx = temp.getContext('2d');
    tctx.drawImage(working, wx1, wy1, ww, wh, 0, 0, ww, wh);
    working.width = ww;
    working.height = wh;
    working.getContext('2d').drawImage(temp, 0, 0);
    setCropRect(null);
    cropStartRef.current = null;
    setEffectiveImageSrc(working.toDataURL('image/png'));
    drawPreviewFromWorking(null);
  }, [cropRect, drawPreviewFromWorking, pushState]);

  // Magic eraser: remove background via Workers AI
  const handleRemoveBackground = useCallback(async () => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0 || working.height === 0) return;
    pushState();
    setRemovingBG(true);
    try {
      const dataUrl = working.toDataURL('image/png');
      const result = await removeBackgroundWorkersAI(dataUrl);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = result;
      });
      working.width = img.width;
      working.height = img.height;
      working.getContext('2d').clearRect(0, 0, working.width, working.height);
      working.getContext('2d').drawImage(img, 0, 0);
      setEffectiveImageSrc(working.toDataURL('image/png'));
      drawPreviewFromWorking(null);
    } catch (err) {
      console.error('Remove background failed:', err);
    } finally {
      setRemovingBG(false);
    }
  }, [drawPreviewFromWorking, pushState]);

  // Remove whiteness around edges: make white/near-white pixels at alpha edges fully transparent (defringe)
  const handleRemoveWhitenessAroundEdges = useCallback(() => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0 || working.height === 0) return;
    pushState();
    const ctx = working.getContext('2d');
    const w = working.width;
    const h = working.height;
    const data = ctx.getImageData(0, 0, w, h);
    const d = data.data;
    const whiteThreshold = 235; // rgb >= this considered "white"
    const edgeAlphaThreshold = 128; // neighbor alpha below this = "at edge"
    const hasTransparentNeighbor = (x, y) => {
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
          const ia = (ny * w + nx) * 4 + 3;
          if (d[ia] < edgeAlphaThreshold) return true;
        }
      }
      return false;
    };
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        if (d[i + 3] < 10) continue;
        if (!hasTransparentNeighbor(x, y)) continue;
        const r = d[i];
        const g = d[i + 1];
        const b = d[i + 2];
        if (r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold) {
          d[i + 3] = 0;
        }
      }
    }
    ctx.putImageData(data, 0, 0);
    setEffectiveImageSrc(working.toDataURL('image/png'));
    drawPreviewFromWorking(cropRectRef.current, hasMagicSelection ? magicSelectionMaskRef.current : null);
  }, [drawPreviewFromWorking, hasMagicSelection, pushState]);

  // Smoothen edges: 3x3 box blur on alpha channel to soften jagged cutout edges
  const handleSmoothenEdges = useCallback(() => {
    const working = workingCanvasRef.current;
    if (!working || working.width === 0 || working.height === 0) return;
    pushState();
    const ctx = working.getContext('2d');
    const w = working.width;
    const h = working.height;
    const data = ctx.getImageData(0, 0, w, h);
    const d = data.data;
    const alphaOut = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
              sum += d[((ny * w + nx) * 4) + 3];
              n++;
            }
          }
        }
        alphaOut[y * w + x] = n > 0 ? sum / n : d[(y * w + x) * 4 + 3];
      }
    }
    for (let i = 0; i < w * h; i++) {
      d[i * 4 + 3] = Math.round(alphaOut[i]);
    }
    ctx.putImageData(data, 0, 0);
    setEffectiveImageSrc(working.toDataURL('image/png'));
    drawPreviewFromWorking(cropRectRef.current, hasMagicSelection ? magicSelectionMaskRef.current : null);
  }, [drawPreviewFromWorking, hasMagicSelection, pushState]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const working = workingCanvasRef.current;
      if (working && working.width > 0 && working.height > 0) {
        onSave(working.toDataURL('image/png'));
      } else {
        const newDataUrl = await applyImageTransform(imageSrc, transform);
        onSave(newDataUrl);
      }
    } catch (err) {
      console.error('Failed to apply transform:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-[#111118] rounded-2xl w-full max-w-[96vw] sm:max-w-[90vw] lg:max-w-6xl max-h-[95vh] flex flex-col border border-white/10 overflow-hidden shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <RotateCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Transform {label}</h2>
              <p className="text-sm text-white/50">Rotate and flip to align</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Preview - flex-1 so it fills the modal */}
        <div className="p-4 sm:p-6 flex-1 flex flex-col min-h-0 overflow-y-auto">
          <div
            ref={containerRef}
            className="relative flex items-center justify-center mb-4 min-h-[280px] flex-1 max-h-[55vh] sm:max-h-[60vh] bg-[#0a0a0f] rounded-xl border border-white/10 overflow-auto select-none"
            style={{
              cursor: tool === 'eraser' || tool === 'crop' || tool === 'magicEraser' || tool === 'magicSelect' || tool === 'bucketFill' ? 'crosshair' : 'default',
              touchAction: tool === 'eraser' || tool === 'crop' || tool === 'magicEraser' || tool === 'magicSelect' || tool === 'bucketFill' ? 'none' : 'auto',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={() => { handlePointerUp(); setEraserCursor(null); }}
            onPointerCancel={handlePointerUp}
          >
            <div
              style={{
                width: viewSize.w * zoom,
                height: viewSize.h * zoom,
                minWidth: '100%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  width: viewSize.w * zoom,
                  height: viewSize.h * zoom,
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
            {/* Eraser size preview circle */}
            {tool === 'eraser' && eraserCursor && (
              <div
                className="absolute pointer-events-none border-2 border-blue-400 rounded-full bg-blue-400/10"
                style={{
                  left: eraserCursor.x - eraserCursor.radius,
                  top: eraserCursor.y - eraserCursor.radius,
                  width: eraserCursor.radius * 2,
                  height: eraserCursor.radius * 2,
                }}
              />
            )}
          </div>

          {/* Zoom & Undo */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm font-medium text-white/60">Zoom:</span>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
              className="p-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
              className="p-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              className="px-3 py-1.5 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors text-sm font-mono"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ml-2 sm:ml-4"
              title="Undo last edit"
            >
              <Undo2 className="w-4 h-4" />
              Undo
            </button>
          </div>

          {/* Tool Tabs: Transform | Crop | Eraser | Magic eraser */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm font-medium text-white/60 mr-2">Tool:</span>
            <button
              type="button"
              onClick={() => setTool('transform')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                tool === 'transform'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <MousePointer2 className="w-4 h-4" />
              Transform
            </button>
            <button
              type="button"
              onClick={() => setTool('crop')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                tool === 'crop'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Crop className="w-4 h-4" />
              Crop
            </button>
            <button
              type="button"
              onClick={() => setTool('eraser')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                tool === 'eraser'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Eraser className="w-4 h-4" />
              Eraser
            </button>
            <button
              type="button"
              onClick={() => setTool('magicEraser')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                tool === 'magicEraser'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              title="Click on a part to remove similar colors (e.g. background, lens). Use tolerance to control reach."
            >
              <Wand2 className="w-4 h-4" />
              Magic eraser
            </button>
            <button
              type="button"
              onClick={() => setTool('magicSelect')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                tool === 'magicSelect'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              title="Click to auto-select similar colors, then Delete selection to erase."
            >
              <Wand2 className="w-4 h-4" />
              Magic select
            </button>
            <button
              type="button"
              onClick={() => setTool('bucketFill')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                tool === 'bucketFill'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              title="Click to fill similar colors with the selected color or transparent."
            >
              <PaintBucket className="w-4 h-4" />
              Bucket fill
            </button>
            {tool === 'crop' && (
              <div className="flex items-center gap-1.5 ml-2">
                <span className="text-xs text-white/50">Aspect:</span>
                {[
                  { label: 'Free', value: null },
                  { label: '1:1', value: 1 },
                  { label: '4:3', value: 4 / 3 },
                  { label: '16:9', value: 16 / 9 },
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setCropAspectRatio(value)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      (cropAspectRatio === value) || (value === null && cropAspectRatio === null)
                        ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-transparent'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {cropRect && cropRect.x2 > cropRect.x1 && cropRect.y2 > cropRect.y1 && (
              <>
                <button
                  type="button"
                  onClick={clearCropSelection}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white/80 border border-white/20 hover:bg-white/15 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear selection
                </button>
                <button
                  type="button"
                  onClick={applyCrop}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Apply crop
                </button>
              </>
            )}
            {hasMagicSelection && (
              <>
                <button
                  type="button"
                  onClick={clearMagicSelection}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white/80 border border-white/20 hover:bg-white/15 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear selection
                </button>
                <button
                  type="button"
                  onClick={applyMagicSelectionErase}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
                >
                  <Eraser className="w-4 h-4" />
                  Delete selection
                </button>
              </>
            )}
            {tool === 'eraser' && (
              <div className="flex items-center gap-2 ml-4">
                <label className="text-sm text-white/60">Size:</label>
                <input
                  type="range"
                  min={4}
                  max={80}
                  value={eraserSize}
                  onChange={(e) => setEraserSize(Number(e.target.value))}
                  className="w-24 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <span className="text-sm text-white/50 font-mono w-8">{eraserSize}</span>
              </div>
            )}
            {tool === 'magicEraser' && (
              <div className="flex flex-wrap items-center gap-2 ml-4">
                <span className="text-xs text-white/40 italic hidden sm:inline">Click part to remove similar colors</span>
                <span className="text-sm text-white/60">Tolerance:</span>
                <div className="flex gap-1">
                  {[
                    { label: 'Low', value: 15 },
                    { label: 'Standard', value: 40 },
                    { label: 'High', value: 75 },
                  ].map(({ label, value }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setMagicEraserTolerance(value)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        magicEraserTolerance === value
                          ? 'bg-blue-500/30 text-blue-300 border border-blue-500/50'
                          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-transparent'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="range"
                  min={5}
                  max={100}
                  value={magicEraserTolerance}
                  onChange={(e) => setMagicEraserTolerance(Number(e.target.value))}
                  className="w-28 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <span className="text-sm text-white/50 font-mono w-8">{magicEraserTolerance}</span>
                <span className="text-sm text-white/60 ml-2">Transparency:</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={magicEraserTransparency}
                  onChange={(e) => setMagicEraserTransparency(Number(e.target.value))}
                  className="w-24 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <span className="text-sm text-white/50 font-mono w-8">{magicEraserTransparency}%</span>
              </div>
            )}
            {tool === 'bucketFill' && (
              <div className="flex flex-wrap items-center gap-2 ml-4">
                <span className="text-sm text-white/60">Fill:</span>
                <button
                  type="button"
                  onClick={() => setBucketFillColor('transparent')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors border ${
                    bucketFillColor === 'transparent'
                      ? 'bg-blue-500/30 text-blue-300 border-blue-500/50'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border-transparent'
                  }`}
                >
                  Transparent
                </button>
                <input
                  type="color"
                  value={bucketFillColor === 'transparent' ? '#ffffff' : bucketFillColor}
                  onChange={(e) => setBucketFillColor(e.target.value)}
                  className="w-9 h-9 rounded cursor-pointer border border-white/20 bg-white/5 p-0.5"
                  title="Fill color"
                />
                <span className="text-xs text-white/40 italic hidden sm:inline">Tolerance: same as Magic eraser</span>
              </div>
            )}
          </div>

          {/* Magic eraser: Remove background */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleRemoveBackground}
              disabled={removingBG}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 text-violet-300 border border-violet-500/30 hover:from-violet-500/30 hover:to-fuchsia-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {removingBG ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Removing background…
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Magic eraser (remove background)
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleRemoveWhitenessAroundEdges}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
              title="Make white pixels at transparent edges fully transparent (removes fringing)"
            >
              <Sun className="w-4 h-4" />
              Remove whiteness around edges
            </button>
            <button
              type="button"
              onClick={handleSmoothenEdges}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
              title="Soften jagged cutout edges with a light alpha blur"
            >
              <Feather className="w-4 h-4" />
              Smoothen edges
            </button>
          </div>

          {/* Rotation Controls */}
          <div className="space-y-4">
            {/* Quick Rotation Buttons */}
            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => handleRotate(-90)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                -90°
              </button>
              <button
                type="button"
                onClick={() => handleRotate(-15)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                -15°
              </button>
              <button
                type="button"
                onClick={() => handleRotate(-1)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                -1°
              </button>
              <div className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 font-mono min-w-[80px] text-center">
                {transform.rotation}°
              </div>
              <button
                type="button"
                onClick={() => handleRotate(1)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                +1°
              </button>
              <button
                type="button"
                onClick={() => handleRotate(15)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                <RotateCw className="w-4 h-4" />
                +15°
              </button>
              <button
                type="button"
                onClick={() => handleRotate(90)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                <RotateCw className="w-4 h-4" />
                +90°
              </button>
            </div>

            {/* Rotation Slider */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white/60">Fine rotation</label>
              <input
                type="range"
                min="-180"
                max="180"
                value={transform.rotation > 180 ? transform.rotation - 360 : transform.rotation}
                onChange={(e) => setTransform(prev => ({ ...prev, rotation: Number(e.target.value) }))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
            </div>

            {/* Flip & Offset Controls */}
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-white/5">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTransform(prev => ({ ...prev, flipH: !prev.flipH }))}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    transform.flipH 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <FlipHorizontal className="w-4 h-4" />
                  Flip H
                </button>
                <button
                  type="button"
                  onClick={() => setTransform(prev => ({ ...prev, flipV: !prev.flipV }))}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    transform.flipV 
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <FlipVertical className="w-4 h-4" />
                  Flip V
                </button>
              </div>

              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reset
              </button>
            </div>

            {/* Offset Controls */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  <Move className="w-3 h-3 inline mr-1" />
                  X Offset
                </label>
                <input
                  type="number"
                  value={transform.offsetX}
                  onChange={(e) => setTransform(prev => ({ ...prev, offsetX: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0f] border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/60 mb-2">
                  <Move className="w-3 h-3 inline mr-1" />
                  Y Offset
                </label>
                <input
                  type="number"
                  value={transform.offsetY}
                  onChange={(e) => setTransform(prev => ({ ...prev, offsetY: Number(e.target.value) }))}
                  className="w-full px-3 py-2 rounded-lg bg-[#0a0a0f] border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-white/5 bg-[#0a0a0f]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Apply Changes
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// IMAGE UPLOAD COMPONENT
// ============================================

const ImageUploader = ({ 
  label, 
  value, 
  onChange, 
  onFileSelect, 
  accept = 'image/*',
  hint,
  required = false,
  allowTransform = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(value || null);
  const [inputMode, setInputMode] = useState(value?.startsWith('http') ? 'url' : 'upload');
  const [showTransformEditor, setShowTransformEditor] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (value?.startsWith('http') || value?.startsWith('data:')) {
      setPreview(value);
    }
  }, [value]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    
    const dataUrl = await fileToDataUrl(file);
    setPreview(dataUrl);
    onFileSelect?.(file, dataUrl);
  }, [onFileSelect]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUrlChange = (url) => {
    onChange(url);
    setPreview(url);
  };

  const handleTransformSave = (newDataUrl) => {
    setPreview(newDataUrl);
    onChange(newDataUrl);
    setShowTransformEditor(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-white/70">
          {label} {required && '*'}
        </label>
        <div className="flex gap-1 bg-white/5 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setInputMode('upload')}
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              inputMode === 'upload' ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white'
            }`}
          >
            <Upload className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => setInputMode('url')}
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              inputMode === 'url' ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white'
            }`}
          >
            <LinkIcon className="w-3 h-3" />
          </button>
        </div>
      </div>

      {inputMode === 'upload' ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
            isDragging 
              ? 'border-blue-500 bg-blue-500/10' 
              : 'border-white/10 hover:border-white/20 bg-[#0a0a0f]'
          }`}
        >
          {preview ? (
            <div className="relative">
              <img 
                src={preview} 
                alt="Preview" 
                className="max-h-32 mx-auto rounded-lg object-contain"
              />
              <div className="absolute -top-2 -right-2 flex gap-1">
                {allowTransform && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTransformEditor(true);
                    }}
                    className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center hover:bg-purple-600 transition-colors"
                    title="Transform image"
                  >
                    <RotateCw className="w-3 h-3" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview(null);
                    onChange('');
                  }}
                  className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          ) : (
            <div className="py-4">
              <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <p className="text-sm text-white/50">
                Drag & drop or <span className="text-blue-400">browse</span>
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleDrop}
            className="hidden"
          />
        </div>
      ) : (
        <input
          type="url"
          value={value || ''}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://..."
          className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
        />
      )}

      {hint && (
        <p className="text-xs text-white/40">{hint}</p>
      )}

      {/* Transform Editor Modal */}
      <AnimatePresence>
        {showTransformEditor && preview && (
          <ImageTransformEditor
            imageSrc={preview}
            label={label}
            onSave={handleTransformSave}
            onClose={() => setShowTransformEditor(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// HINGE POSITION PICKER
// ============================================

// Content rect when img uses object-contain: actual drawn image is centered and may not fill the element
function getContentRect(img) {
  if (!img || !img.naturalWidth || !img.naturalHeight) return null;
  const r = img.getBoundingClientRect();
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  const scale = Math.min(r.width / nw, r.height / nh);
  const cw = nw * scale;
  const ch = nh * scale;
  const ox = (r.width - cw) / 2;
  const oy = (r.height - ch) / 2;
  return { contentX: ox, contentY: oy, contentWidth: cw, contentHeight: ch, imgWidth: r.width, imgHeight: r.height };
}

const HingePositionPicker = ({ imageSrc, hingeX, hingeY, onChange }) => {
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [alphaBounds, setAlphaBounds] = useState(null); // { ok, xMin, xMax, yMin, yMax, scaleX, scaleY, centerX, centerY }
  const [markerPos, setMarkerPos] = useState({ leftX: 0, rightX: 0, y: 0 });
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Coerce to number so we always use full precision (no string/rounding from API)
  const x = Number(hingeX);
  const y = Number(hingeY);

  // Reset imgLoaded when imageSrc changes to force recalculation
  useEffect(() => {
    setImgLoaded(false);
    // Check if image is already loaded (cached)
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setImgLoaded(true);
      setUpdateTrigger(t => t + 1);
    }
  }, [imageSrc]);

  // Update marker positions - recalculate from scratch each time
  const updateMarkerPositions = useCallback(() => {
    const img = imgRef.current;
    const wrapper = wrapperRef.current;
    if (!img || !wrapper || !imgLoaded) return;
    if (!img.naturalWidth || !img.naturalHeight) return;

    const rect = getContentRect(img);
    if (!rect) return;

    const { contentX, contentY, contentWidth, contentHeight } = rect;
    const imgRect = img.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const imgOffsetX = imgRect.left - wrapperRect.left;
    const imgOffsetY = imgRect.top - wrapperRect.top;

    // Calculate aspect ratio fresh each time
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    // hingeX 0..1 = distance from CENTER of VISIBLE (alpha) content (0=center, 1=edge)
    // If we can detect alpha bounds, map to that; otherwise fall back to full image.
    const b = alphaBounds?.ok ? alphaBounds : null;
    const centerX = b ? b.centerX : 0.5;
    const halfWidth = b ? (b.scaleX / 2) : 0.5;
    const contentRightX = centerX + x * halfWidth;
    const contentLeftX = centerX - x * halfWidth;
    const rightX = imgOffsetX + contentX + contentRightX * contentWidth;
    const leftX = imgOffsetX + contentX + contentLeftX * contentWidth;

    // hingeY world coords → yPercent 0=top 1=bottom → content pixel
    const worldHeight = 2 / aspectRatio;
    const yPercent = ((worldHeight / 2) - y) / worldHeight;
    const markerY = imgOffsetY + contentY + (yPercent * contentHeight);

    setMarkerPos({ leftX, rightX, y: markerY });
  }, [x, y, imgLoaded, alphaBounds]);

  // Update markers when values change or image loads
  useEffect(() => {
    updateMarkerPositions();
  }, [updateMarkerPositions]);

  // Watch for container/image resize
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !imgLoaded) return;

    const resizeObserver = new ResizeObserver(() => {
      setUpdateTrigger(t => t + 1);
      updateMarkerPositions();
    });

    resizeObserver.observe(img);
    return () => resizeObserver.disconnect();
  }, [imgLoaded, updateMarkerPositions]);

  const handleClick = (e) => {
    if (!imgLoaded) return;

    const img = imgRef.current;
    if (!img || !img.naturalWidth || !img.naturalHeight) return;

    const rect = getContentRect(img);
    if (!rect) return;

    const { contentX, contentY, contentWidth, contentHeight } = rect;
    const clickX = e.clientX - img.getBoundingClientRect().left;
    const clickY = e.clientY - img.getBoundingClientRect().top;

    // Click in content rect coordinates (0..1)
    const contentNormX = (clickX - contentX) / contentWidth;
    const contentNormY = (clickY - contentY) / contentHeight;
    const clampedX = Math.max(0, Math.min(1, contentNormX));
    const clampedY = Math.max(0, Math.min(1, contentNormY));

    // If alpha bounds exist, clamp click into the visible content bbox.
    const b = alphaBounds?.ok ? alphaBounds : null;
    const xFull = b ? Math.max(b.xMin, Math.min(b.xMax, clampedX)) : clampedX;
    const yFull = b ? Math.max(b.yMin, Math.min(b.yMax, clampedY)) : clampedY;

    // hingeX: 0=center, 1=edge of visible content (alpha bbox)
    const centerX = b ? b.centerX : 0.5;
    const halfWidth = b ? (b.scaleX / 2) : 0.5;
    const rawHingeX = halfWidth > 1e-6 ? Math.abs(xFull - centerX) / halfWidth : 0;
    const newHingeX = Number(Math.min(1, Math.max(0, rawHingeX)).toFixed(3));

    // hingeY: world coords - calculate aspect ratio fresh
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    const worldHeight = 2 / aspectRatio;
    const rawHingeY = (worldHeight / 2) - (yFull * worldHeight);
    const newHingeY = Number(rawHingeY.toFixed(3));

    onChange({ hingeX: newHingeX, hingeY: newHingeY });
  };

  const handleImageLoad = () => {
    setImgLoaded(true);
    // Precompute alpha bounds once per image (fixes hinge placement for padded PNGs)
    try {
      const img = imgRef.current;
      if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
        const b = computeAlphaBoundsFromImage(img, { alphaThreshold: 20, maxScanSize: 512 });
        setAlphaBounds(b);
      } else {
        setAlphaBounds(null);
      }
    } catch {
      setAlphaBounds(null);
    }
    // Trigger marker update after image loads
    setTimeout(() => setUpdateTrigger(t => t + 1), 0);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-white/70">
        Hinge Position (click on frame to set)
      </label>
      <div 
        ref={wrapperRef}
        className="relative bg-[#0a0a0f] rounded-xl border border-white/10 overflow-hidden cursor-crosshair flex items-center justify-center"
        onClick={handleClick}
        style={{ minHeight: '150px' }}
      >
        {imageSrc ? (
          <>
            <img 
              ref={imgRef}
              src={imageSrc} 
              alt="Frame" 
              className="max-w-full max-h-[200px] object-contain"
              onLoad={handleImageLoad}
            />
            {imgLoaded && (
              <>
                {/* Hinge marker - right side */}
                <div 
                  className="absolute w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-lg pointer-events-none"
                  style={{ 
                    left: `${markerPos.rightX}px`,
                    top: `${markerPos.y}px`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
                {/* Hinge marker - left side */}
                <div 
                  className="absolute w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-lg pointer-events-none"
                  style={{ 
                    left: `${markerPos.leftX}px`,
                    top: `${markerPos.y}px`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
                {/* Horizontal guide line */}
                <div 
                  className="absolute h-px bg-blue-500/30 pointer-events-none"
                  style={{ 
                    left: `${markerPos.leftX}px`,
                    width: `${markerPos.rightX - markerPos.leftX}px`,
                    top: `${markerPos.y}px`,
                  }}
                />
              </>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-32 text-white/30">
            <p className="text-sm">Upload front image first</p>
          </div>
        )}
      </div>
      <p className="text-xs text-white/40 mt-2">
        Click where the temple arm should attach. Blue dots show hinge points on both sides.
      </p>
    </div>
  );
};

// ============================================
// IMAGE PREVIEW MODAL
// ============================================

const ImagePreviewModal = ({ src, alt, onClose }) => {
  if (!src) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative max-w-4xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={src} 
          alt={alt} 
          className="max-w-full max-h-[90vh] object-contain rounded-xl"
        />
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// VTO PREVIEW MODAL
// ============================================

const VTOPreviewModal = ({ products, selectedProduct, onClose }) => {
  // Convert user products to VTO catalog format
  const catalog = products
    .filter((p) => {
      const has2DImage = !!p.frontImageUrl;
      const has3DModel = !!p.modelUrl;
      return has2DImage || has3DModel;
    })
    .map(p => ({
      id: p.id,
      name: p.name || p.sku,
      src: p.frontImageUrl,
      handleSrc: p.handleImageUrl,
      category: p.category || 'dioptric',
      gender: p.gender || 'unisex',
      addToCartUrl: p.addToCartUrl || null,
      frameWidth: p.frameWidth || 140, // Frame width in mm
      handleWidth: p.handleWidth || 140, // Temple length in mm
      bridgeLengthCm: p.bridgeLengthCm ?? 1.8, // Bridge length in cm
      frontContentScaleX: p.frontContentScaleX ?? 1,
      handleContentScaleX: p.handleContentScaleX ?? 1,
      scaleCorrection: p.scaleCorrection ?? 1,
      templeHingeOffset: p.templeHingeOffset ?? 0.95, // Where temples attach to frame (0-1, edge=1)
      templeYOffset: p.templeYOffset ?? 0.02, // Vertical offset of temples
      templeLength: p.templeLength ?? 3, // Temple arm length
      recommendedForFaceShapes: p.recommendedFaceShapes || [],
      // Clip-on
      clipOnSrc: p.clipOnImageUrl || null,
      clipOnHingeOffset: p.clipOnHingeOffset ?? 0.95,
      clipOnYOffset: p.clipOnYOffset ?? 0.02,
      // 3D model support
      assetType: p.assetType || (p.modelUrl ? '3d' : '2d'),
      modelUrl: p.modelUrl || null,
    }));

  // Reorder so selected product is first
  if (selectedProduct) {
    const selectedIndex = catalog.findIndex(p => p.id === selectedProduct.id);
    if (selectedIndex > 0) {
      const [selected] = catalog.splice(selectedIndex, 1);
      catalog.unshift(selected);
    }
  }

  if (catalog.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/95"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="bg-[#111118] rounded-2xl p-8 max-w-md text-center border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          <Glasses className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No Products Available</h2>
          <p className="text-white/60 mb-6">
            Add products with front images and set them as active to try them in VTO.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            Close
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/95"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-[#0a0a0f] rounded-2xl w-full max-w-6xl h-[90vh] overflow-hidden border border-white/10 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5 bg-[#111118]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Your VTO Experience</h2>
              <p className="text-sm text-white/50">
                {catalog.length} product{catalog.length !== 1 ? 's' : ''} loaded
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* VTO Container */}
        <div className="flex-1 overflow-hidden">
          <VirtualTryOn 
            catalog={catalog}
            embedded={true}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================
// PRODUCT LIST ROW (for list view, optional drag handle)
// ============================================

const ProductListItem = ({
  product,
  index,
  orderMode,
  onEdit,
  onDelete,
  onViewImage,
  onTryVTO,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
}) => {
  const thumbnailSrc = product.frontImageUrl;
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: isDragging ? 0.6 : 1 }}
      className={`flex items-center gap-4 p-4 rounded-xl bg-[#111118] border border-white/5 hover:border-blue-500/30 transition-colors ${
        orderMode ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      draggable={orderMode}
      onDragStart={orderMode ? (e) => onDragStart(e, index) : undefined}
      onDragOver={orderMode ? onDragOver : undefined}
      onDrop={orderMode ? (e) => onDrop(e, index) : undefined}
    >
      {orderMode && (
        <div className="flex-shrink-0 text-white/40 hover:text-white/70 cursor-grab active:cursor-grabbing" title="Drag to reorder">
          <GripVertical className="w-5 h-5" />
        </div>
      )}
      <div
        className="w-20 h-14 flex-shrink-0 bg-[#0a0a0f] rounded-lg flex items-center justify-center overflow-hidden cursor-pointer"
        onClick={() => thumbnailSrc && onViewImage(thumbnailSrc, product.name)}
      >
        {thumbnailSrc ? (
          <img src={thumbnailSrc} alt={product.name} className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="w-8 h-8 text-white/20" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-white truncate">{product.name || 'Untitled'}</h3>
        <p className="text-sm text-white/40">SKU: {product.sku}</p>
      </div>
      <div className="flex flex-wrap gap-1.5 flex-shrink-0">
        <span className="px-2 py-0.5 rounded text-xs font-medium text-white/70">
          {product.category === 'sunglasses' ? 'Sunglasses' : 'Prescription'}
        </span>
        <span className="px-2 py-0.5 rounded text-xs font-medium text-white/70">
          {product.gender === 'male' ? 'Men' : product.gender === 'female' ? 'Women' : 'Unisex'}
        </span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${product.active ? 'bg-emerald-500/10 text-emerald-400' : 'text-white/40'}`}>
          {product.active ? 'Active' : 'Inactive'}
        </span>
      </div>
      {!orderMode && (
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => onTryVTO(product)}
            className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 transition-colors"
            title="Try VTO"
          >
            <Play className="w-4 h-4 fill-white" />
          </button>
          <button
            onClick={() => onEdit(product)}
            className="p-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(product.id, product.name)}
            className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

// ============================================
// PRODUCT CARD COMPONENT
// ============================================

const ProductCard = ({ product, index, onEdit, onDelete, onViewImage, onTryVTO }) => {
  const thumbnailSrc = product.frontImageUrl;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-[#111118] rounded-2xl border border-white/5 overflow-hidden hover:border-blue-500/30 transition-colors group"
    >
      {/* Thumbnail */}
      <div 
        className="aspect-video bg-[#0a0a0f] flex items-center justify-center relative cursor-pointer"
        onClick={() => thumbnailSrc && onViewImage(thumbnailSrc, product.name)}
      >
        {thumbnailSrc ? (
          <>
            <img
              src={thumbnailSrc}
              alt={product.name}
              className="w-full h-full object-contain p-4"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <ZoomIn className="w-8 h-8 text-white" />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center text-white/20">
            {product.assetType === '3d' ? (
              <Box className="w-12 h-12" />
            ) : (
              <ImageIcon className="w-12 h-12" />
            )}
            <span className="text-xs mt-2">No preview</span>
          </div>
        )}
        <div className="absolute top-2 right-2 flex flex-wrap gap-1 justify-end">
          <span className="px-2 py-1 rounded-md bg-black/50 text-xs font-medium text-white/70">
            {product.category === 'sunglasses' ? 'Sunglasses' : 'Prescription'}
          </span>
          <span className="px-2 py-1 rounded-md bg-black/50 text-xs font-medium text-white/70">
            {product.gender === 'male' ? 'Men' : product.gender === 'female' ? 'Women' : 'Unisex'}
          </span>
          <span className="px-2 py-1 rounded-md bg-black/50 text-xs font-medium text-white/70">
            {product.assetType.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-bold text-white">
              {product.name || 'Untitled'}
            </h3>
            <p className="text-sm text-white/40">SKU: {product.sku}</p>
          </div>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              product.active
                ? 'bg-emerald-500/10 text-emerald-400'
                : 'bg-white/5 text-white/40'
            }`}
          >
            {product.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Image Links */}
        {product.assetType === '2d' && (
          <div className="flex gap-2 mt-3 mb-3">
            {product.frontImageUrl && (
              <button
                onClick={() => onViewImage(product.frontImageUrl, 'Front Image')}
                className="flex items-center gap-1 px-2 py-1 rounded bg-white/5 text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Eye className="w-3 h-3" />
                Front
              </button>
            )}
            {product.handleImageUrl && (
              <button
                onClick={() => onViewImage(product.handleImageUrl, 'Handle Image')}
                className="flex items-center gap-1 px-2 py-1 rounded bg-white/5 text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Eye className="w-3 h-3" />
                Handle
              </button>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
          {/* Try VTO Button */}
          <button
            onClick={() => onTryVTO(product)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium hover:from-blue-600 hover:to-cyan-600 transition-all"
          >
            <Play className="w-3.5 h-3.5 fill-white" />
            Try VTO
          </button>
          <button
            onClick={() => onEdit(product)}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => onDelete(product.id, product.name)}
            className="flex items-center justify-center p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// MAIN PRODUCTS PAGE
// ============================================

// Category filter: all | dioptric (prescription) | sunglasses
const CATEGORY_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'dioptric', label: 'Prescription' },
  { value: 'sunglasses', label: 'Sunglasses' },
];

// Gender filter: all | male (men + unisex) | female (women + unisex)
const GENDER_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'male', label: 'Men' },
  { value: 'female', label: 'Women' },
];

const PAGE_SIZE = 24;
const REORDER_FETCH_LIMIT = 2000;
const VTO_FETCH_PAGE_LIMIT = 100;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [searchInput, setSearchInput] = useState(''); // controlled input
  const [searchQuery, setSearchQuery] = useState(''); // debounced, sent to API
  const loadIdRef = useRef(0); // ignore stale responses (race when search/filter changes)
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [orderMode, setOrderMode] = useState(false); // when true: list view + draggable reorder, fetch all
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showVTOModal, setShowVTOModal] = useState(false);
  const [selectedProductForVTO, setSelectedProductForVTO] = useState(null);
  const [vtoCatalogProducts, setVtoCatalogProducts] = useState([]); // full catalog for "Try Your VTO"
  const [editingProduct, setEditingProduct] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  // Debounce search: update searchQuery 400ms after user stops typing; reset to page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadProducts = useCallback(async (opts = {}) => {
    const category = opts.category ?? categoryFilter;
    const gender = opts.gender ?? genderFilter;
    const pageNum = opts.page ?? page;
    const limit = orderMode ? REORDER_FETCH_LIMIT : (opts.limit ?? PAGE_SIZE);
    const q = opts.q !== undefined ? opts.q : searchQuery;
    const currentId = ++loadIdRef.current;
    setLoading(true);
    try {
      const data = await productsApi.list(category, gender, { page: pageNum, limit, q: q || undefined });
      // Ignore if a newer request already ran (e.g. user changed search before this returned)
      if (currentId !== loadIdRef.current) return;
      setProducts(data.products || []);
      setTotal(data.total ?? 0);
      if (opts.page != null) setPage(opts.page);
    } catch (err) {
      if (currentId !== loadIdRef.current) return;
      console.error('Failed to load products:', err);
      setProducts([]);
      setTotal(0);
    } finally {
      if (currentId === loadIdRef.current) setLoading(false);
    }
  }, [categoryFilter, genderFilter, page, orderMode, searchQuery]);

  useEffect(() => {
    loadProducts();
  }, [categoryFilter, genderFilter, searchQuery, orderMode, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showPagination = !orderMode && total > PAGE_SIZE;

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name || 'this product'}"?`)) return;

    try {
      await productsApi.delete(id);
      const wasLastOnPage = products.length === 1 && page > 1;
      await loadProducts({ page: wasLastOnPage ? Math.max(1, page - 1) : page });
      setSuccess('Product deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSave = async (productData) => {
    try {
      if (editingProduct) {
        await productsApi.update(editingProduct.id, productData);
        setProducts(
          products.map((p) =>
            p.id === editingProduct.id ? { ...p, ...productData } : p
          )
        );
        setSuccess('Product updated');
      } else {
        await productsApi.create(productData);
        setSuccess('Product created');
        await loadProducts({ page: 1 });
      }
      setShowModal(false);
      setEditingProduct(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      throw error;
    }
  };

  const handleViewImage = (src, alt) => {
    setPreviewImage({ src, alt });
  };

  const handleTryVTO = (product) => {
    setSelectedProductForVTO(product);
    openVTOWithAllProducts(product);
  };

  const fetchAllProductsForVTO = useCallback(async () => {
    try {
      const allData = await productsApi.list('all', 'all', { all: true });
      const full = Array.isArray(allData?.products) ? allData.products : [];
      if (full.length > 0) {
        const seen = new Set();
        return full.filter((p) => {
          if (!p?.id || seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
      }
    } catch {
      // Fall through to paginated fallback.
    }

    const allProducts = [];
    let pageNum = 1;
    const seenIds = new Set();

    // The backend /vto/products endpoint is paginated (max 100 per page),
    // so fetch every page explicitly for the VTO modal catalog.
    // Always use all/all here: in-modal model filtering handles category/gender.
    while (true) {
      const data = await productsApi.list('all', 'all', {
        limit: VTO_FETCH_PAGE_LIMIT,
        page: pageNum,
      });
      const batch = Array.isArray(data?.products) ? data.products : [];
      batch.forEach((product) => {
        if (!product?.id || seenIds.has(product.id)) return;
        seenIds.add(product.id);
        allProducts.push(product);
      });

      if (batch.length < VTO_FETCH_PAGE_LIMIT) break;
      pageNum += 1;

      // Safety guard in case backend pagination behaves unexpectedly.
      if (pageNum > 200) break;
    }

    return allProducts;
  }, []);

  const openVTOWithAllProducts = async (selectedProduct = null) => {
    setSelectedProductForVTO(selectedProduct);
    try {
      const catalog = await fetchAllProductsForVTO();
      setVtoCatalogProducts(catalog);
      setShowVTOModal(true);
    } catch (err) {
      setError(err.message || 'Failed to load catalog for VTO');
    }
  };

  const handleReorderStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleReorderOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleReorderDrop = async (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    setDraggedIndex(null);
    if (fromIndex == null || fromIndex === dropIndex) return;
    const prev = [...products];
    const next = [...products];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(dropIndex, 0, removed);
    setProducts(next);
    setReorderSaving(true);
    try {
      await productsApi.reorder(next.map((p) => p.id));
      setSuccess('Order saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save order');
      setProducts(prev);
    } finally {
      setReorderSaving(false);
    }
  };

  const handleSetOrderMode = () => {
    setPage(1);
    setOrderMode(true);
    setViewMode('list');
  };

  const handleSetViewMode = (mode) => {
    setViewMode(mode);
    if (mode !== 'list') {
      setOrderMode(false);
      setPage(1);
    }
  };

  // Full-page spinner only on initial load; during search/filter keep showing current list
  const isInitialLoad = loading && products.length === 0 && total === 0;
  if (isInitialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Products</h1>
          <p className="text-white/60">
            Manage your glasses catalog for virtual try-on.
          </p>
        </div>
        <div className="flex gap-3">
          {total > 0 && (
            <button
              onClick={openVTOWithAllProducts}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:from-blue-600 hover:to-cyan-600 transition-all flex items-center gap-2 shadow-lg shadow-blue-500/20"
            >
              <Play className="w-4 h-4 fill-white" />
              Try Your VTO
            </button>
          )}
          <button
            onClick={() => {
              setEditingProduct(null);
              setShowModal(true);
            }}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>
      </motion.div>

      {/* Status Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-900/20 rounded-xl p-4 border border-red-500/20 flex items-center gap-3 mb-6"
          >
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-300">{error}</p>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-emerald-900/20 rounded-xl p-4 border border-emerald-500/20 flex items-center gap-3 mb-6"
          >
            <Check className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="search"
            placeholder="Search by name or SKU..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {loading && <Loader2 className="w-5 h-5 text-white/50 animate-spin" aria-hidden />}
            {!loading && searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="text-white/40 hover:text-white"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Category, Gender, and View/Order toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-white/60 mr-2">Category:</span>
            {CATEGORY_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => { setCategoryFilter(value); setPage(1); }}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  categoryFilter === value
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-white/60 mr-2">Gender:</span>
            {GENDER_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => { setGenderFilter(value); setPage(1); }}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  genderFilter === value
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-white/60 mr-2 hidden sm:inline">View:</span>
          <button
            type="button"
            onClick={() => handleSetViewMode('grid')}
            className={`p-2.5 rounded-xl transition-colors ${
              viewMode === 'grid' && !orderMode
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
            title="Grid view"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => handleSetViewMode('list')}
            className={`p-2.5 rounded-xl transition-colors ${
              viewMode === 'list' && !orderMode
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
            title="List view"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={handleSetOrderMode}
            className={`p-2.5 rounded-xl transition-colors flex items-center gap-2 ${
              orderMode
                ? 'bg-amber-500 text-white border border-amber-500'
                : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
            title="Reorder (switches to list view)"
          >
            <GripVertical className="w-5 h-5" />
            <span className="text-sm font-medium hidden sm:inline">Reorder</span>
          </button>
          {orderMode && (
            <button
              type="button"
              onClick={() => setOrderMode(false)}
              className="p-2.5 rounded-xl bg-white/5 text-white/70 hover:bg-white/10 border border-white/10 text-sm font-medium"
            >
              Done
            </button>
          )}
        </div>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111118] rounded-2xl p-12 border border-white/5 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <Glasses className="w-10 h-10 text-white/20" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">No products yet</h2>
          <p className="text-white/60 mb-6">
            Add your first glasses product to get started with virtual try-on.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all"
          >
            Add Your First Product
          </button>
        </motion.div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
              onEdit={(p) => {
                setEditingProduct(p);
                setShowModal(true);
              }}
              onDelete={handleDelete}
              onViewImage={handleViewImage}
              onTryVTO={handleTryVTO}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {orderMode && (
            <p className="text-sm text-amber-400/90 mb-3 flex items-center gap-2">
              <GripVertical className="w-4 h-4" />
              Drag rows to change order. Changes are saved automatically.
              {reorderSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            </p>
          )}
          {products.map((product, index) => (
            <ProductListItem
              key={product.id}
              product={product}
              index={index}
              orderMode={orderMode}
              onEdit={(p) => {
                setEditingProduct(p);
                setShowModal(true);
              }}
              onDelete={handleDelete}
              onViewImage={handleViewImage}
              onTryVTO={handleTryVTO}
              onDragStart={handleReorderStart}
              onDragOver={handleReorderOver}
              onDrop={handleReorderDrop}
              isDragging={draggedIndex === index}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {showPagination && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-6 border-t border-white/10"
        >
          <p className="text-sm text-white/60">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2.5 rounded-xl bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10 disabled:opacity-40 disabled:pointer-events-none transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-white/80 font-medium px-2">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2.5 rounded-xl bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10 disabled:opacity-40 disabled:pointer-events-none transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Product Modal */}
      <AnimatePresence>
        {showModal && (
          <ProductModal
            product={editingProduct}
            onClose={() => {
              setShowModal(false);
              setEditingProduct(null);
            }}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>

      {/* Image Preview Modal */}
      <AnimatePresence>
        {previewImage && (
          <ImagePreviewModal
            src={previewImage.src}
            alt={previewImage.alt}
            onClose={() => setPreviewImage(null)}
          />
        )}
      </AnimatePresence>

      {/* VTO Preview Modal */}
      <AnimatePresence>
        {showVTOModal && (
          <VTOPreviewModal
            products={vtoCatalogProducts}
            selectedProduct={selectedProductForVTO}
            onClose={() => {
              setShowVTOModal(false);
              setSelectedProductForVTO(null);
              setVtoCatalogProducts([]);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// PRODUCT MODAL COMPONENT
// ============================================

const ProductModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    sku: product?.sku || '',
    name: product?.name || '',
    assetType: product?.assetType || '2d',
    modelUrl: product?.modelUrl || '',
    frontImageUrl: product?.frontImageUrl || '',
    handleImageUrl: product?.handleImageUrl || '',
    addToCartUrl: product?.addToCartUrl || '',
    category: product?.category || 'dioptric',
    gender: product?.gender || 'unisex', // male | female | unisex
    frameWidth: product?.frameWidth ?? 140,
    handleWidth: product?.handleWidth ?? 140, // Temple/handle length in mm (typical: 135-150mm)
    bridgeLengthCm: product?.bridgeLengthCm ?? 1.8, // Bridge length in cm (typical: ~1.4-2.4cm)
    templeLength: product?.templeLength ?? 3,
    templeHingeOffset: product?.templeHingeOffset != null ? Number(product.templeHingeOffset) : 0.95,
    templeYOffset: product?.templeYOffset != null ? Number(product.templeYOffset) : 0.02,
    templeHandleHeight: product?.templeHandleHeight ?? 2,
    // Asset padding / scaling correction (computed from alpha-bounds; optional manual override)
    frontContentScaleX: product?.frontContentScaleX ?? 1,
    frontContentScaleY: product?.frontContentScaleY ?? 1,
    frontContentBBox: product?.frontContentBBox ?? null, // JSON string or null
    handleContentScaleX: product?.handleContentScaleX ?? 1,
    handleContentScaleY: product?.handleContentScaleY ?? 1,
    handleContentBBox: product?.handleContentBBox ?? null, // JSON string or null
    scaleCorrection: product?.scaleCorrection ?? 1,
    recommendedFaceShapes: product?.recommendedFaceShapes || [],
    active: product?.active ?? true,
    clipOnImageUrl: product?.clipOnImageUrl || '',
    clipOnHingeOffset: product?.clipOnHingeOffset != null ? Number(product.clipOnHingeOffset) : 0.95,
    clipOnYOffset: product?.clipOnYOffset != null ? Number(product.clipOnYOffset) : 0.02,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleFrontImageSelect = (file, dataUrl) => {
    setFormData(prev => ({ ...prev, frontImageUrl: dataUrl }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // For now, we're using data URLs directly
      // In production, you'd upload to R2 first
      const payload = { ...formData };
      // Compute alpha-bounds for scale correction (only when we can read pixels, e.g. data URLs)
      try {
        if (payload.frontImageUrl && String(payload.frontImageUrl).startsWith('data:')) {
          const b = await computeAlphaBoundsFromDataUrl(payload.frontImageUrl, { alphaThreshold: 20, maxScanSize: 512 });
          if (b?.ok) {
            payload.frontContentScaleX = Number((b.scaleX || 1).toFixed(4));
            payload.frontContentScaleY = Number((b.scaleY || 1).toFixed(4));
            payload.frontContentBBox = JSON.stringify({ xMin: b.xMin, yMin: b.yMin, xMax: b.xMax, yMax: b.yMax });
          }
        }
      } catch {
        // ignore
      }
      try {
        if (payload.handleImageUrl && String(payload.handleImageUrl).startsWith('data:')) {
          const b = await computeAlphaBoundsFromDataUrl(payload.handleImageUrl, { alphaThreshold: 20, maxScanSize: 512 });
          if (b?.ok) {
            payload.handleContentScaleX = Number((b.scaleX || 1).toFixed(4));
            payload.handleContentScaleY = Number((b.scaleY || 1).toFixed(4));
            payload.handleContentBBox = JSON.stringify({ xMin: b.xMin, yMin: b.yMin, xMax: b.xMax, yMax: b.yMax });
          }
        }
      } catch {
        // ignore
      }

      // Clip-on: send null when no image (or only sentinel whitespace)
      if (!payload.clipOnImageUrl?.trim()) payload.clipOnImageUrl = null;

      await onSave(payload);
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const faceShapes = ['oval', 'round', 'square', 'heart', 'oblong'];

  const modalContent = (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={product ? 'Edit Product' : 'Add Product'}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        transition={{ type: 'tween', duration: 0.2 }}
        className="bg-[#111118] rounded-2xl w-full max-w-2xl border border-white/10 my-8 max-h-[90vh] overflow-y-auto shadow-2xl relative z-10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5 sticky top-0 bg-[#111118] z-10">
          <h2 className="text-xl font-bold text-white">
            {product ? 'Edit Product' : 'Add Product'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-900/20 rounded-lg p-3 border border-red-500/20 text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                SKU *
              </label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                placeholder="e.g., RB-3025"
                required
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Classic Aviator"
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
            </div>
          </div>

          {/* Add to Cart URL */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Add to Cart URL
              <span className="text-white/40 text-xs ml-2">(Optional - Shows cart button in widget)</span>
            </label>
            <input
              type="url"
              value={formData.addToCartUrl || ''}
              onChange={(e) => setFormData({ ...formData, addToCartUrl: e.target.value })}
              placeholder="https://yourshop.com/cart/add?sku=RB-3025"
              className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
            />
            <p className="mt-2 text-xs text-white/40">
              Enter the URL where customers can add this product to their cart. If provided, a cart button will appear in the VTO widget.
            </p>
          </div>

          {/* Category: Prescription / Sunglasses */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Category
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, category: 'dioptric' })}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  formData.category === 'dioptric'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Glasses className="w-4 h-4" />
                Prescription
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, category: 'sunglasses' })}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  formData.category === 'sunglasses'
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Sun className="w-4 h-4" />
                Sunglasses
              </button>
            </div>
          </div>

          {/* Gender: Men / Women / Unisex */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Gender
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'male' })}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  formData.gender === 'male'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                Men
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'female' })}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  formData.gender === 'female'
                    ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                Women
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'unisex' })}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  formData.gender === 'unisex'
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                Unisex
              </button>
            </div>
          </div>

          {/* Front Image */}
          <ImageUploader
            label="Front Image (transparent PNG)"
            value={formData.frontImageUrl}
            onChange={(url) => setFormData({ ...formData, frontImageUrl: url })}
            onFileSelect={handleFrontImageSelect}
            accept="image/png,image/webp"
            hint="Recommended: transparent PNG, facing camera. Click ↻ to rotate/transform."
            required
            allowTransform={true}
          />

          {/* Handle Image */}
          <ImageUploader
            label="Handle/Temple Image (transparent PNG)"
            value={formData.handleImageUrl}
            onChange={(url) => setFormData({ ...formData, handleImageUrl: url })}
            onFileSelect={(_, dataUrl) => {
              setFormData(prev => ({ ...prev, handleImageUrl: dataUrl }));
            }}
            accept="image/png,image/webp"
            hint="Recommended: transparent PNG, side view of temple arm. Click ↻ to rotate/transform."
            required
            allowTransform={true}
          />

          {/* Clip-on Section */}
          <div className="p-4 rounded-xl bg-[#0a0a0f] border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-white">Clip-on Available</p>
                <p className="text-sm text-white/40">Enable if this frame has a clip-on attachment</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, clipOnImageUrl: prev.clipOnImageUrl !== '' ? '' : ' ' }))}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  formData.clipOnImageUrl !== '' ? 'bg-amber-500' : 'bg-white/10'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                    formData.clipOnImageUrl !== '' ? 'left-6' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {formData.clipOnImageUrl !== '' && (
              <>
                <ImageUploader
                  label="Clip-on Front Image (transparent PNG)"
                  value={formData.clipOnImageUrl}
                  onChange={(url) => setFormData(prev => ({ ...prev, clipOnImageUrl: url }))}
                  onFileSelect={(_, dataUrl) => {
                    setFormData(prev => ({ ...prev, clipOnImageUrl: dataUrl }));
                  }}
                  accept="image/png,image/webp"
                  hint="Front-facing image of the clip-on lens attachment. Click ↻ to rotate/transform."
                  allowTransform={true}
                />

                <HingePositionPicker
                  imageSrc={formData.clipOnImageUrl}
                  hingeX={formData.clipOnHingeOffset}
                  hingeY={formData.clipOnYOffset}
                  onChange={({ hingeX, hingeY }) => setFormData(prev => ({
                    ...prev,
                    clipOnHingeOffset: Number(hingeX),
                    clipOnYOffset: Number(hingeY),
                  }))}
                />
              </>
            )}
          </div>

          {/* Dimensions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Frame Width (mm)
              </label>
              <input
                type="number"
                value={formData.frameWidth}
                onChange={(e) => setFormData({ ...formData, frameWidth: Number(e.target.value) || 140 })}
                min="100"
                max="200"
                step="1"
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
              <p className="text-xs text-white/40 mt-1">Total width of frame front (130-150mm typical)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Temple Length (mm)
              </label>
              <input
                type="number"
                value={formData.handleWidth}
                onChange={(e) => setFormData({ ...formData, handleWidth: Number(e.target.value) || 140 })}
                min="100"
                max="180"
                step="1"
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
              <p className="text-xs text-white/40 mt-1">Length of temple arm (135-150mm typical)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Bridge Length (cm)
              </label>
              <input
                type="number"
                value={formData.bridgeLengthCm}
                onChange={(e) => setFormData({ ...formData, bridgeLengthCm: Number(e.target.value) || 1.8 })}
                min="0.8"
                max="3.5"
                step="0.1"
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
              <p className="text-xs text-white/40 mt-1">Bridge width between lenses (≈ 1.4–2.4cm typical)</p>
            </div>
          </div>

          {/* Scale correction (handles transparent padding) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Scale Correction
              </label>
              <input
                type="number"
                value={formData.scaleCorrection}
                onChange={(e) => setFormData({ ...formData, scaleCorrection: Number(e.target.value) || 1 })}
                min="0.8"
                max="1.3"
                step="0.01"
                className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
              />
              <p className="text-xs text-white/40 mt-1">
                Optional. Use to fine-tune fit if a frame still looks slightly off.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Detected Content Width
              </label>
              <div className="w-full px-4 py-3 rounded-xl bg-[#0a0a0f] border border-white/10 text-white/80 font-mono">
                {Number(formData.frontContentScaleX || 1).toFixed(3)}
              </div>
              <p className="text-xs text-white/40 mt-1">
                Auto-calculated from transparency (higher means less padding).
              </p>
            </div>
          </div>

          {/* Hinge Position Picker */}
          <HingePositionPicker
            imageSrc={formData.frontImageUrl}
            hingeX={formData.templeHingeOffset}
            hingeY={formData.templeYOffset}
            onChange={({ hingeX, hingeY }) => setFormData({
              ...formData,
              templeHingeOffset: Number(hingeX),
              templeYOffset: Number(hingeY),
            })}
          />

          {/* Face Shapes */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Recommended Face Shapes
            </label>
            <div className="flex flex-wrap gap-2">
              {faceShapes.map((shape) => (
                <button
                  key={shape}
                  type="button"
                  onClick={() => {
                    const shapes = formData.recommendedFaceShapes?.includes(shape)
                      ? formData.recommendedFaceShapes.filter((s) => s !== shape)
                      : [...(formData.recommendedFaceShapes || []), shape];
                    setFormData({ ...formData, recommendedFaceShapes: shapes });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                    formData.recommendedFaceShapes?.includes(shape)
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/20'
                  }`}
                >
                  {shape}
                </button>
              ))}
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-[#0a0a0f] border border-white/10">
            <div>
              <p className="font-medium text-white">Active</p>
              <p className="text-sm text-white/40">
                Only active products appear in widget
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, active: !formData.active })}
              className={`w-12 h-7 rounded-full transition-colors relative ${
                formData.active ? 'bg-blue-500' : 'bg-white/10'
              }`}
            >
              <span
                className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                  formData.active ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-white/5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 text-white hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : product ? 'Update' : 'Create'}
            </button>
          </div>
        </form>


      </motion.div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ProductsPage;
